// Lightweight client-side image compression so base64 uploads stay under ~700KB each.
// Returns a data: URL (JPEG, quality-adjusted) or the original if compression isn't worth it.
const MAX_BYTES = 700 * 1024; // ~700KB final size
const MAX_DIMENSION = 1600;   // px — long-edge cap

export async function compressImage(file, { maxBytes = MAX_BYTES, maxDimension = MAX_DIMENSION } = {}) {
    if (!file || !file.type.startsWith("image/")) {
        return readAsDataUrl(file);
    }

    const bitmap = await loadBitmap(file);
    const { width, height } = scaleDown(bitmap.width, bitmap.height, maxDimension);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    // Iteratively reduce quality until we're under the size cap
    let quality = 0.9;
    let dataUrl = canvas.toDataURL("image/jpeg", quality);
    while (estimateBase64Bytes(dataUrl) > maxBytes && quality > 0.4) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL("image/jpeg", quality);
    }
    return dataUrl;
}

function loadBitmap(file) {
    if (typeof createImageBitmap === "function") {
        return createImageBitmap(file);
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        img.src = url;
    });
}

function scaleDown(w, h, maxDim) {
    if (w <= maxDim && h <= maxDim) return { width: w, height: h };
    const ratio = w > h ? maxDim / w : maxDim / h;
    return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function estimateBase64Bytes(dataUrl) {
    const commaIdx = dataUrl.indexOf(",");
    const b64Len = commaIdx >= 0 ? dataUrl.length - commaIdx - 1 : dataUrl.length;
    return Math.ceil(b64Len * 3 / 4);
}

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

// Convert a `data:image/...;base64,xxx` URL back into a Blob for multipart upload.
export function dataUrlToBlob(dataUrl) {
    const [meta, b64] = dataUrl.split(",");
    const mime = (meta.match(/data:([^;]+);base64/) || [])[1] || "image/jpeg";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

// Compress (client-side) then upload to GCS via FastAPI; returns the public URL string.
// Falls back to a base64 data URL if the upload endpoint is unavailable so the admin UI
// keeps functioning end-to-end during local dev.
export async function uploadImage(file, api, folder = "images") {
    const dataUrl = await compressImage(file);
    if (!api) return dataUrl;
    try {
        const blob = dataUrlToBlob(dataUrl);
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const filename = (file.name || `image.${ext}`).replace(/\.[^.]+$/, `.${ext}`);
        const form = new FormData();
        form.append("file", new File([blob], filename, { type: blob.type }));
        const res = await api.post(`/uploads/image?folder=${encodeURIComponent(folder)}`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res?.data?.url || dataUrl;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("uploadImage falling back to base64:", err?.response?.data || err?.message);
        return dataUrl;
    }
}
